import { extend } from 'lodash';
// Enums
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
// Models
import { WorkItemChatPinInvite } from './models/workitem-chat-pin-invite-model';
import { WorkItemLens } from './models/workitem-lens';
import { EmailItem } from './interfaces/email-item.interface';
import { WorkItem } from './interfaces/workitem-base.interface';
import { ChatItem } from './interfaces/chat-item.interface';
import { SmsItem } from './interfaces/sms-item.interface';
import { FacebookItem } from './interfaces/facebook-item.interface';
import { AsyncMessageItem } from './interfaces/async-message-item.interface';
import { WorkItemDiscussion } from './models/work-item-discussion';
import { WorkItemComposeEmail } from './models/workitem-compose-email-model';
// Services
import { ThemeServiceHelper } from 'common/theme/service.helper';
import { LogoutSubscriber } from 'shared/services/subscribers/logout-subscriber.service';
import { WorkItemStateHandler } from 'shared/services/workitem-state-handler/workitem-state-handler.service';
import { WorkItemsTrackingService } from './workitems-tracking.service';
import { DiscussionItem } from './interfaces/discussion-item.interface';
import { Injectable } from '@angular/core';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { BcRestApi } from 'common/api/rest-api';
import Moment from 'moment';
import { EmailsService } from 'common/emails/emails.service';
import { BcConfigService } from 'common/config/provider';

@Injectable({
  providedIn: 'root'
})
export class WorkItemListService {
  private _chatPinviteWorkItemList: WorkItemChatPinInvite[] = [];
  private _emailComposeList: WorkItemComposeEmail[] = [];
  private _emailWorkItemList: EmailItem[] = [];
  private _discussionWorkItemList: WorkItemDiscussion[] = [];

  public bcEnums;
  public moment;
  public chatWorkItemList;
  public lensWorkItemList: WorkItemLens[];
  public textChatWorkItemList;
  public unansweredMessageCount;
  public secondsRemainingDisplay;
  public showSecondsRemaining;
  public disableAccordionRedDot;

  constructor(private config: BcConfigService, private bcAnalytics: BcAnalytics, private bcRestApi: BcRestApi,
              private xlcEmails: EmailsService, LogoutSubscriber: LogoutSubscriber,
              private WorkItemStateHandler: WorkItemStateHandler,
              private bcWorkItemsTrackingService: WorkItemsTrackingService) {
    this.moment = Moment;

    this.chatWorkItemList = [];
    this.lensWorkItemList = [];
    this.textChatWorkItemList = [];

    this.init();

    LogoutSubscriber.subscribe(this._onLogout.bind(this));
  }

  init() {
    this.unansweredMessageCount = 0;
    this.secondsRemainingDisplay = '';
    this.showSecondsRemaining = false;
    this.chatWorkItemList.length = 0;
    this.disableAccordionRedDot = false;
    this.textChatWorkItemList.length = 0;
  }

  resetState() {
    this.init();
  }

  resetChats() {
    this.chatWorkItemList.length = 0;
  }

  resetTextChats() {
    this.textChatWorkItemList.length = 0;
  }

  _onLogout() {
    this.resetState();
  }

  getChatWorkItemList() {
    return this.chatWorkItemList;
  }

  set chatPinviteWorkItemList(pinviteList: WorkItemChatPinInvite[]) {
    this._chatPinviteWorkItemList = pinviteList;
  }

  set emailWorkItemList(emailList: EmailItem[]) {
    this._emailWorkItemList = emailList;
  }

  set discussionWorkItemList(discussionList: WorkItemDiscussion[]) {
    this._discussionWorkItemList = discussionList;
  }

  set emailComposeList(composeItemList: WorkItemComposeEmail[]) {
    this._emailComposeList = composeItemList;
  }

  getChatPinviteWorkitemList(): WorkItemChatPinInvite[] {
    return this._chatPinviteWorkItemList;
  }

  getChatWorkitemCount() {
    return this.chatWorkItemList.length;
  }

  getDiscussionWorkItemCount(): number {
    return this._discussionWorkItemList.length;
  }

  private getEmailWorkitemList(): EmailItem[] {
    return this._emailWorkItemList;
  }

  getEmailWorkitemCount(): number {
    return this._emailWorkItemList ? this._emailWorkItemList.length : 0;
  }

  getTextChatWorkitemList() {
    return this.textChatWorkItemList;
  }

  getTextChatWorkitemCount() {
    return this.textChatWorkItemList.length;
  }

  getAllWorkitemList(): WorkItem[] {
    let itemsToReturn: WorkItem[] = this.getLensWorkItemList();

    itemsToReturn = itemsToReturn
      .concat(this.getChatPinviteWorkitemList())
      .concat(this.getChatWorkItemList())
      .concat(this.getTextChatWorkitemList())
      .concat(this._emailComposeList)
      .concat(this.getEmailWorkitemList());

    return itemsToReturn;
  }

  getLensWorkItemList(): WorkItemLens[] {
    return this.lensWorkItemList;
  }

  getActiveWorkitemCount(): number {
    return this.getChatWorkitemCount() + this.getEmailWorkitemCount() + this.getTextChatWorkitemCount() + this.getDiscussionWorkItemCount();
  }

  getUnansweredMessageCount() {
    return this.unansweredMessageCount;
  }

  getSecondsRemainingDisplay() {
    return this.secondsRemainingDisplay;
  }

  getShowSecondsRemaining() {
    return this.showSecondsRemaining;
  }

  addTimerToItemIfApplicable(item) {
    if (item && !item.IsAnswered && !item.secondsRemainingTimerPromise) {
      const context = this;
      const timerDisplaySecondsRemainingThreshold = this.config.current.timerDisplaySecondsRemainingThreshold;

      // Prime the pump: set the seconds remaining on the item
      const acdAssignmentExpires = item.ACDAssignmentExpires ? this.getAdjustedExpireDate(item) : 0;
      let timeRemainingInSeconds = this.getDeltaTimeInSeconds(acdAssignmentExpires);

      item.showSecondsRemaining = item.showSecondsRemaining && timeRemainingInSeconds > 0;
      item.secondsRemainingDisplay = this.getTimeRemainingForDisplay(timeRemainingInSeconds);
      item.secondsRemainingTimerPromise = setInterval(() => {
        timeRemainingInSeconds = context.getDeltaTimeInSeconds(acdAssignmentExpires);

        item.secondsRemainingDisplay = context.getTimeRemainingForDisplay(timeRemainingInSeconds);

        if ((timeRemainingInSeconds - 1) < 0) {
          context.stopTimerForItem(item);
        }

        item.showSecondsRemaining = (timeRemainingInSeconds <= timerDisplaySecondsRemainingThreshold && timeRemainingInSeconds > 0);

        context.updateParentUnansweredTimeout();
      }, 100);
    }
  }

  getAdjustedExpireDate(item) {
    const currentUnixDate = this.moment().unix();
    const acdAssignmentExpires = new Date(item.ACDAssignmentExpires).getTime() / 1000;
    const updatedDate = new Date(item.Updated).getTime() / 1000;

    return acdAssignmentExpires + Math.ceil(currentUnixDate - updatedDate);
  }

  getDeltaTimeInSeconds(unixExpireDate) {
    return unixExpireDate - this.moment().unix();
  }

  getTimeRemainingForDisplay(timeRemainingInSeconds) {
    const millisecondsRemaining = timeRemainingInSeconds * 1000;
    const dateFormat = timeRemainingInSeconds >= 60 ? 'm:ss' : ':ss';
    return this.moment(millisecondsRemaining).format(dateFormat);
  }

  generateWorkitemClass(workitem) {
    const itemId = workitem.id;
    const isSelected = itemId === this.getSelectedWorkitemId();
    const isNew = workitem.NewItem;
    const isOpened = this.WorkItemStateHandler.isOpened(workitem.id);
    const isEnded = workitem.isEnded;
    const isIdle = workitem.IdleState;

    return ThemeServiceHelper.getCssClass(itemId, isSelected, isNew, isOpened, isEnded, isIdle);
  }

  getSelectedWorkitem() {
    return this.WorkItemStateHandler.getFocusedWorkItem();
  }

  getSelectedWorkitemId() {
    const selectedWorkItem = this.getSelectedWorkitem();
    return selectedWorkItem ? selectedWorkItem.id : null;
  }

  stopTimerForItem(item) {
    if (item && item.secondsRemainingTimerPromise !== null) {
      item.showSecondsRemaining = false;
      clearInterval(item.secondsRemainingTimerPromise);
      item.secondsRemainingTimerPromise = null;

      this.updateParentUnansweredTimeout();
    }
  }

  acceptChatItem(workItem: ChatItem) {
    if (!workItem.IsAnswered) {
      this.bcRestApi.answerChat(workItem.id);
      this.stopTimerForItem(workItem);
      this.bcAnalytics.eventTrack('WorkItemAnswered', { type: WorkItemTypes.CHAT });
    }
  }

  acceptEmailItem(workItem: EmailItem) {
    if (workItem.type !== WorkItemTypes.COMPOSE_EMAIL) {
      if (!workItem.IsAnswered && !workItem.isEnded) {
        this.xlcEmails.acceptEmailThread(workItem.id);
        this.bcAnalytics.eventTrack('EmailThreadAccepted');
      }
    }
  }

  acceptSmsItem(workItem: SmsItem) {
    if (!workItem.IsAnswered) {
      this.bcRestApi.answerTextChat(workItem.id);
      this.stopTimerForItem(workItem);
      this.bcAnalytics.eventTrack('WorkItemAnswered', { type: WorkItemTypes.SMS });
    }
  }

  acceptFacebookItem(workItem: FacebookItem) {
    if (!workItem.IsAnswered) {
      this.bcRestApi.answerTextChat(workItem.id);
      this.stopTimerForItem(workItem);
      this.bcAnalytics.eventTrack('WorkItemAnswered', { type: WorkItemTypes.FACEBOOK });
    }
  }

  acceptAsyncMessageItem(workItem: AsyncMessageItem) {
    if (!workItem.IsAnswered) {
      this.bcRestApi.answerTextChat(workItem.id);
      this.stopTimerForItem(workItem);
      this.bcAnalytics.eventTrack('WorkItemAnswered', { type: WorkItemTypes.ASYNC_MESSAGE });
    }
  }

  acceptDiscussionItem(workItem: DiscussionItem) {
    if (!workItem.Accepted) {
      this.bcRestApi.acceptDiscussion(workItem.ItemID);
      this.bcAnalytics.eventTrack('WorkItemAnswered', { type: WorkItemTypes.DISCUSSION });
    }
  }

  selectWorkitem(workItem: WorkItem) {
    const isSelected = workItem.id === this.getSelectedWorkitemId();
    if (!isSelected) {
      this.bcWorkItemsTrackingService.eventTrack(workItem);
    }
    this.WorkItemStateHandler.openWorkItem(workItem.id);
    this.WorkItemStateHandler.focusWorkItem(workItem.id);
  }

  focusWorkItem(workItem: WorkItem) {
    const isSelected = workItem.id === this.getSelectedWorkitemId();
    this.WorkItemStateHandler.focusWorkItem(workItem.id);
    if (!isSelected) {
      this.bcWorkItemsTrackingService.eventTrack(workItem);
    }
  }

  selectAndAcceptChatItem(workItem: ChatItem) {
    this.acceptChatItem(workItem);
    this.selectWorkitem(workItem);
  }

  selectAndAcceptEmailItem(workItem: EmailItem) {
    this.acceptEmailItem(workItem);
    this.selectWorkitem(workItem);
  }

  selectAndAcceptDiscussionItem(workItem: DiscussionItem) {
    this.acceptDiscussionItem(workItem);
    this.selectWorkitem(workItem);
  }

  selectAndAcceptFacebookWorkItem(workItem: FacebookItem) {
    this.acceptFacebookItem(workItem);
    this.selectWorkitem(workItem);
  }

  selectAndAcceptAsyncMessageWorkItem(workItem: AsyncMessageItem) {
    this.acceptAsyncMessageItem(workItem);
    this.selectWorkitem(workItem);
  }

  selectAndAcceptSmsWorkItem(workItem: SmsItem) {
    this.acceptSmsItem(workItem);
    this.selectWorkitem(workItem);
  }

  removeWorkItem(workItem: WorkItem) {
    this.WorkItemStateHandler.removeWorkItem(workItem.id);
  }

  updateWorkItem(workitem, changes) {
    if (workitem) {
      extend(workitem, changes);
      workitem.IsAnswered = this.isAnswered(changes);
    }
  }

  isAnswered(workitem) {
    return !!workitem.Answered || !!workitem.Ended;
  }

  createLensSession(workItem: WorkItem) {
    this.selectWorkitem(workItem);
  }

  isLiveSessionActive() {
    return this._chatPinviteWorkItemList.length !== 0 || this.lensWorkItemList.length !== 0 || this._isRemoteControlSessionActive();
  }

  private _isRemoteControlSessionActive(): boolean {
    return this.chatWorkItemList.filter(workItem => workItem.hasActiveRemoteControl).length > 0;
  }

  updateParentUnansweredTimeout() {
    let itemsWithSecsRemaining = this.chatWorkItemList.filter((workitem) => !!workitem.showSecondsRemaining);
    itemsWithSecsRemaining = itemsWithSecsRemaining.concat(this.textChatWorkItemList.filter((workitem) => !!workitem.showSecondsRemaining));

    this.displayWorkItemsNotification(itemsWithSecsRemaining);
  }

  displayWorkItemsNotification(workItems) {
    if (workItems && workItems.length > 0) {
      let leastSrd = 9999;
      let leastWorkItemSrd = '';

      workItems.forEach(workItem => {
        if (workItem.secondsRemainingDisplay && workItem.secondsRemainingDisplay.length > 0) {
          const srd = Number(workItem.secondsRemainingDisplay.replace(':', ''));
          if (!isNaN(srd) && srd < leastSrd) {
            leastWorkItemSrd = workItem.secondsRemainingDisplay;
            leastSrd = srd;
          }
        }
      });

      this.secondsRemainingDisplay = leastWorkItemSrd;
      this.showSecondsRemaining = true;
    } else {
      this.secondsRemainingDisplay = '';
      this.showSecondsRemaining = false;
    }
  }

  updateParentUnansweredMessageCount() {
    let count = this.getNewWorkItemsCount(this.chatWorkItemList);
    count += this.getNewWorkItemsCount(this.textChatWorkItemList);
    if (count === 0) {
      count = this.getGreatestCountOfUnansweredWorkItemsByServiceType();
    }
    this.unansweredMessageCount = this.getOverrideAccordionNotificationCount(count);
    this.updateRedDotNotificationForAccordion();
  }

  getOverrideAccordionNotificationCount(count) {
    return count > 0 ? 1 : 0;
  }

  private getNewWorkItemsCount(workItems) {
    return Object.keys(workItems)
      .filter(workitemId => !!workItems[workitemId] && workItems[workitemId].NewItem && !workItems[workitemId].IsAnswered).length;
  }

  getRedDotNotificationForAccordionDisabledValue() {
    return this.disableAccordionRedDot;
  }

  updateRedDotNotificationForAccordion() {
    const hasNewWorkItems = this.getNewWorkItemsCount(this.chatWorkItemList) + this.getNewWorkItemsCount(this.textChatWorkItemList);
    const isAllWorkItemsEnded = this.isAllWorkItemsWithMessagesEnded();

    if (!hasNewWorkItems) {
      this.disableAccordionRedDot = isAllWorkItemsEnded;
    } else {
      this.disableAccordionRedDot = false;
    }
  }

  isAllWorkItemsWithMessagesEnded() {
    return !this.chatWorkItemList.some(workItem => workItem.unansweredMessages > 0 && !workItem.Ended);
  }

  getGreatestCountOfUnansweredWorkItemsByServiceType() {
    const workItems = this.chatWorkItemList;
    let count = 0;
    let workItem;

    for (const key of workItems) {
      if (count < 3) {
        workItem = this.chatWorkItemList[key];
        if (workItem) {
          if (workItem.unansweredMessages > 0) {
            count = workItem.unansweredMessages;
          }
          if (count > 2) {
            break;
          }
        }
      }
    }

    return count;
  }
}
